import React, { useRef, useState } from "react";
import i18n from "i18next";
import { LuUpload } from "react-icons/lu";
import { connect } from "react-redux";
import {
  addNavItem as addNavItemAction,
  addSound as addSoundAction,
  updateViewToolbar as updateViewToolbarAction,
  uploadFile as uploadFileAction,
} from "../../../../../../common/actions";
import { parseAndUploadZip, createScenesFromGuide } from "./zipImporter";
import { isPage } from "../../../../../../common/utils";
import { isFileTooLarge, fileTooLargeMessage } from "../../../../../../common/upload_limits.es6";

function ZipImportModal(props) {
  const {
    onImportComplete,
    onBoxAdded,
    boxes,
    addNavItem,
    addSound,
    updateViewToolbar,
    uploadFile,
    navItemsIds,
    indexSelected,
    viewToolbars,
    showScenePicker,
  } = props;
  const inputRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState({ pct: 0, label: "" });
  const [error, setError] = useState(null);
  // Explicit "insert after which scene" picker (only shown when showScenePicker).
  // "" means append at the end. Defaults to the currently-selected scene.
  const sceneIds = (Array.isArray(navItemsIds) ? navItemsIds : []).filter((id) => isPage(id));
  const [selectedAfter, setSelectedAfter] = useState(() =>
    isPage(indexSelected) ? indexSelected : ""
  );

  async function handleFile(file) {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      setStatus("error");
      setError(i18n.t("ZipImport.invalid_file") || "Not a valid ZIP file");
      return;
    }
    if (isFileTooLarge(file)) {
      setStatus("error");
      setError(fileTooLargeMessage());
      return;
    }
    setStatus("working");
    setError(null);
    setProgress({ pct: 0, label: i18n.t("ZipImport.starting") || "Starting…" });
    try {
      const { guide, urls } = await parseAndUploadZip(file, setProgress);
      // Compute section insertion position: after the picked scene (when the
      // picker is shown) or the currently-selected nav item, else at end.
      const ids = Array.isArray(navItemsIds) ? navItemsIds : [];
      const sel = showScenePicker ? selectedAfter : indexSelected;
      const sectionPosition = sel && ids.indexOf(sel) !== -1 ? ids.indexOf(sel) + 1 : ids.length;
      createScenesFromGuide(guide, urls, {
        addNavItem,
        addSound,
        updateViewToolbar,
        uploadFile,
        onBoxAdded,
        boxes,
        sectionPosition,
      });
      setStatus("done");
      if (onImportComplete) onImportComplete();
    } catch (err) {
      setStatus("error");
      setError((err && err.message) || i18n.t("ZipImport.failed") || "Import failed");
    }
  }

  return (
    <div className="zip-import">
      {status === "idle" && (
        <div className="zip-import__idle">
          {showScenePicker && (
            <div className="zip-import__scene-picker">
              <label htmlFor="zipSceneAfter">{i18n.t("ZipImport.select_scene")}</label>
              <select
                id="zipSceneAfter"
                value={selectedAfter}
                onChange={(e) => setSelectedAfter(e.target.value)}
              >
                {sceneIds.map((id, i) => {
                  const name =
                    (viewToolbars && viewToolbars[id] && viewToolbars[id].viewName) ||
                    i18n.t("slide");
                  return (
                    <option key={id} value={id}>
                      {i + 1}. {name}
                    </option>
                  );
                })}
                <option value="">{i18n.t("ZipImport.at_end")}</option>
              </select>
            </div>
          )}
          <div
            className="zip-import__dropzone"
            onClick={() => inputRef.current && inputRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFile(e.dataTransfer.files[0]);
            }}
          >
            <span className="zip-import__icon">
              <LuUpload size={26} aria-hidden="true" />
            </span>
            <div className="zip-import__title">
              {i18n.t("ZipImport.drop_zip") || "Drop screen recording ZIP here"}
            </div>
            <div className="zip-import__hint">
              {i18n.t("ZipImport.hint") ||
                "Screen recording ZIP exported from the recorder extension"}
            </div>
          </div>
        </div>
      )}

      {status === "working" && (
        <div className="zip-import__progress">
          <div className="zip-import__bar">
            <div className="zip-import__bar-fill" style={{ width: progress.pct + "%" }} />
          </div>
          <div className="zip-import__label">{progress.label}</div>
        </div>
      )}

      {status === "done" && (
        <div className="zip-import__done">
          <i className="material-icons" style={{ fontSize: 48, color: "#22c55e" }}>
            check_circle
          </i>
          <div>{i18n.t("ZipImport.success") || "Import complete!"}</div>
        </div>
      )}

      {status === "error" && (
        <div className="zip-import__error">
          <i className="material-icons" style={{ fontSize: 48, color: "#ef4444" }}>
            error
          </i>
          <div>{error}</div>
          <button
            type="button"
            onClick={() => {
              setStatus("idle");
              setError(null);
            }}
          >
            {i18n.t("ZipImport.try_again") || "Try again"}
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files[0])}
      />
    </div>
  );
}

function mapStateToProps(state) {
  const present = state && state.undoGroup && state.undoGroup.present;
  return {
    navItemsIds: (present && present.navItemsIds) || [],
    indexSelected: present && present.indexSelected,
    viewToolbars: (present && present.viewToolbarsById) || {},
  };
}

function mapDispatchToProps(dispatch) {
  return {
    addNavItem: (...args) => dispatch(addNavItemAction(...args)),
    addSound: (id, value) => dispatch(addSoundAction(id, value)),
    updateViewToolbar: (id, elements) => dispatch(updateViewToolbarAction(id, elements)),
    uploadFile: (id, url, name, keywords, mimetype) =>
      dispatch(uploadFileAction(id, url, name, keywords, mimetype)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ZipImportModal);
